import React, { useState, useEffect } from 'react';
import './App.css';
import { useAuth } from '../components/auth';
import ResidentList, { ResidentMenuItem } from './components/ResidentList';
import PropertyInfoSection from './components/PropertyInfoSection';
import LeaseInfoSection from './components/LeaseInfoSection';
import { ResidentContactList, ResidentContactForm, useResidentContacts, ContactWithDetails } from './components/contacts';
import { useResidentData } from './hooks/useResidentData';
import '../components/common/CRMForm.css';

// Define the items for the sidebar
const MENU_ITEMS: ResidentMenuItem[] = [
  { id: 1, name: 'Property', description: 'View property details' },
  { id: 2, name: 'Lease', description: 'View lease details' },
];

type ActiveView = 'property' | 'lease' | 'contacts';

const AppContent: React.FC<{ user: any; logout: () => Promise<void> }> = ({ user, logout }) => {
  // Default to Property view
  const [activeView, setActiveView] = useState<ActiveView>('property');
  const [selectedItemId, setSelectedItemId] = useState<number>(1);
  const [selectedContact, setSelectedContact] = useState<ContactWithDetails | null>(null);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  
  const {
    contacts,
    error: contactsError,
    globalLoading: contactsLoading,
    loadingMessage: contactsLoadingMessage,
    refetch: refetchContacts,
    deleteContact,
  } = useResidentContacts();

  const { property, fetchProperty } = useResidentData();

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const handleSelectItem = (item: ResidentMenuItem) => {
    setSelectedItemId(item.id);
    if (item.name === 'Property') setActiveView('property');
    else if (item.name === 'Lease') setActiveView('lease');
  };

  const handleSelectContact = (contact: ContactWithDetails) => {
    setSelectedContact(contact);
  };

  const handleContactSave = () => {
    refetchContacts();
    setSelectedContact(null);
    setIsCreatingContact(false);
  };

  const handleContactCancel = () => {
    setSelectedContact(null);
    setIsCreatingContact(false);
  };

  const handleCreateNew = () => {
    setSelectedContact(null);
    setIsCreatingContact(true);
  };

  const handleDeleteContact = async (contact: ContactWithDetails) => {
    if (window.confirm(`Are you sure you want to delete ${contact.firstName} ${contact.lastName}? This action cannot be undone.`)) {
      try {
        await deleteContact(contact.id);
        if (selectedContact?.id === contact.id) {
          setSelectedContact(null);
        }
      } catch (err) {
        console.error('Error deleting contact:', err);
      }
    }
  };

  const renderContent = () => {
    switch (activeView) {
      case 'property':
        return (
          <>
             <div className="form-header">
               <h2>Property Information</h2>
             </div>
             <div className="form-content">
               <PropertyInfoSection />
             </div>
          </>
        );
      case 'lease':
        return (
          <>
             <div className="form-header">
               <h2>Lease Information</h2>
             </div>
             <div className="form-content">
               <LeaseInfoSection />
             </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="resident-app app">
      <header className="app-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Resident Portal</h1>
            <p>View your property, lease, and manage your contact information</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {user && (
              <>
                <span style={{ color: '#64748b', fontSize: '0.875rem' }}>{user.email}</span>
                <button
                  onClick={async () => {
                    await logout();
                  }}
                  className="app-nav-btn"
                  style={{ marginLeft: '1rem' }}
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
        
        <nav className="app-nav">
          <button 
            className={`app-nav-btn ${activeView !== 'contacts' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('property');
              setSelectedItemId(1);
              setSelectedContact(null);
            }}
          >
            Home
          </button>
          <button 
            className={`app-nav-btn ${activeView === 'contacts' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('contacts');
              setSelectedContact(null);
              setIsCreatingContact(false);
            }}
          >
            Contacts
          </button>
        </nav>
      </header>

      <main className="app-main">
        {activeView === 'contacts' ? (
          <>
            <div className="app-sidebar">
              <ResidentContactList
                contacts={contacts}
                error={contactsError}
                selectedContactId={selectedContact?.id}
                onSelectContact={handleSelectContact}
                onDeleteContact={handleDeleteContact}
                onCreateNew={handleCreateNew}
                globalLoading={contactsLoading}
                loadingMessage={contactsLoadingMessage}
                property={property}
              />
            </div>

            <div className="app-content">
              {selectedContact || isCreatingContact ? (
                <ResidentContactForm
                  contact={selectedContact}
                  onSave={handleContactSave}
                  onCancel={handleContactCancel}
                />
              ) : (
                <div className="form">
                  <div className="form-header">
                    <h2>Contact Management</h2>
                  </div>
                  <div className="form-content">
                    <div className="app-welcome-message">
                      <p>Select a contact from the list to view and edit details, or create a new contact.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Sidebar */}
            <div className="app-sidebar">
              <ResidentList 
                items={MENU_ITEMS}
                selectedItemId={selectedItemId}
                onSelectItem={handleSelectItem}
              />
            </div>

            {/* Main Content */}
            <div className="app-content">
              <div className="form">
                {renderContent()}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  const { user, logout } = useAuth();

  if (!user) {
    return null;
  }

  return <AppContent user={user} logout={logout} />;
};

export default App;
